import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router";
import {
  CalendarDays,
  MapPin,
  Building2,
  Users,
  Plus,
  Search,
  ArrowRight,
  MoreHorizontal,
  Pencil,
  Copy,
  Trash2,
  Activity,
  Clock,
  Archive,
  X,
  Image,
} from "lucide-react";
import { tryAdminEventsList, adminEventsService } from "../../services/adminEventsService";
import { useAdminAuth, canManageAllEvents } from "../auth/AdminAuthContext";
import type { Event } from "../../types";
import { compareEventsByNearestStart, compareEventsForListing } from "@mvk/shared";
import { AdminEventCover, eventCoverSrc } from "../components/AdminEventCover";

const statusConfig = {
  active: { label: "Идёт сейчас", bg: "bg-emerald-50", text: "text-emerald-700", dot: "bg-emerald-500" },
  upcoming: { label: "Предстоит", bg: "bg-blue-50", text: "text-blue-700", dot: "bg-blue-500" },
  past: { label: "Завершено", bg: "bg-gray-100", text: "text-gray-500", dot: "bg-gray-400" },
};

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString("ru-RU", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

interface CreateEventModalProps {
  onClose: () => void;
  onCreated: (event: Event) => void;
}

function CreateEventModal({ onClose, onCreated }: CreateEventModalProps) {
  const [form, setForm] = useState({
    title: "",
    industry: "",
    venueName: "",
    city: "",
    startDate: "",
    endDate: "",
    description: "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const handleCreate = async () => {
    if (!form.title || !form.city || !form.startDate || !form.endDate) {
      setError("Заполните обязательные поля");
      return;
    }
    setSaving(true);
    setError("");
    try {
      const event = await adminEventsService.create({
        title: form.title,
        industry: form.industry || "Общее",
        venueName: form.venueName,
        city: form.city,
        startDate: new Date(form.startDate).toISOString(),
        endDate: new Date(form.endDate).toISOString(),
        description: form.description,
        status: "draft",
      });
      onCreated(event);
      onClose();
    } catch {
      setError("Не удалось создать. Нужны права администратора и запущенный API.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h2 className="text-[16px] text-gray-900">Новое мероприятие</h2>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400">
            <X size={18} />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {/* Hero image upload area */}
          <div className="h-32 rounded-xl border-2 border-dashed border-gray-200 flex flex-col items-center justify-center gap-2 cursor-pointer hover:border-indigo-300 hover:bg-indigo-50/30 transition-colors">
            <Image size={24} className="text-gray-300" />
            <span className="text-[13px] text-gray-400">Загрузить обложку мероприятия</span>
            <span className="text-[11px] text-gray-300">JPG, PNG · рекомендуется 1920×600</span>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <label className="text-[12px] text-gray-500 mb-1 block">Название*</label>
              <input
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="TechExpo 2027"
                className="w-full px-3 py-2 rounded-lg border border-gray-200 text-[13px] text-gray-900 placeholder-gray-400 outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100"
              />
            </div>
            <div>
              <label className="text-[12px] text-gray-500 mb-1 block">Отрасль*</label>
              <input
                value={form.industry}
                onChange={(e) => setForm({ ...form, industry: e.target.value })}
                placeholder="Технологии"
                className="w-full px-3 py-2 rounded-lg border border-gray-200 text-[13px] text-gray-900 placeholder-gray-400 outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100"
              />
            </div>
            <div>
              <label className="text-[12px] text-gray-500 mb-1 block">Город*</label>
              <input
                value={form.city}
                onChange={(e) => setForm({ ...form, city: e.target.value })}
                placeholder="Москва"
                className="w-full px-3 py-2 rounded-lg border border-gray-200 text-[13px] text-gray-900 placeholder-gray-400 outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100"
              />
            </div>
            <div className="col-span-2">
              <label className="text-[12px] text-gray-500 mb-1 block">Площадка</label>
              <input
                value={form.venueName}
                onChange={(e) => setForm({ ...form, venueName: e.target.value })}
                placeholder="Экспоцентр"
                className="w-full px-3 py-2 rounded-lg border border-gray-200 text-[13px] text-gray-900 placeholder-gray-400 outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100"
              />
            </div>
            <div>
              <label className="text-[12px] text-gray-500 mb-1 block">Дата начала*</label>
              <input
                type="date"
                value={form.startDate}
                onChange={(e) => setForm({ ...form, startDate: e.target.value })}
                className="w-full px-3 py-2 rounded-lg border border-gray-200 text-[13px] text-gray-900 outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100"
              />
            </div>
            <div>
              <label className="text-[12px] text-gray-500 mb-1 block">Дата окончания*</label>
              <input
                type="date"
                value={form.endDate}
                onChange={(e) => setForm({ ...form, endDate: e.target.value })}
                className="w-full px-3 py-2 rounded-lg border border-gray-200 text-[13px] text-gray-900 outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100"
              />
            </div>
            <div className="col-span-2">
              <label className="text-[12px] text-gray-500 mb-1 block">Описание</label>
              <textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                rows={3}
                placeholder="Краткое описание мероприятия для участников..."
                className="w-full px-3 py-2 rounded-lg border border-gray-200 text-[13px] text-gray-900 placeholder-gray-400 outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 resize-none"
              />
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 px-5 py-4 border-t border-gray-100">
          {error && <p className="text-[12px] text-red-500 mr-auto">{error}</p>}
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg text-[13px] text-gray-600 hover:bg-gray-100 transition-colors"
          >
            Отмена
          </button>
          <button
            onClick={handleCreate}
            disabled={saving}
            className="px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 text-white text-[13px] transition-colors"
          >
            {saving ? "Создание…" : "Создать мероприятие"}
          </button>
        </div>
      </div>
    </div>
  );
}

function eventOverlapsRange(event: Event, from: string, to: string): boolean {
  const start = new Date(event.startDate).getTime();
  const end = new Date(event.endDate).getTime();
  if (from) {
    const fromTs = new Date(from).setHours(0, 0, 0, 0);
    if (end < fromTs) return false;
  }
  if (to) {
    const toTs = new Date(to).setHours(23, 59, 59, 999);
    if (start > toTs) return false;
  }
  return true;
}

export function AdminEventsPage() {
  const navigate = useNavigate();
  const { user } = useAdminAuth();
  const [events, setEvents] = useState<Event[]>([]);
  const [search, setSearch] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [filterStatus, setFilterStatus] = useState<string | null>("actual");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [loading, setLoading] = useState(true);

  const loadEvents = useCallback(async () => {
    setLoading(true);
    const fromApi = await tryAdminEventsList();
    setEvents(fromApi ?? []);
    setLoading(false);
  }, []);

  useEffect(() => {
    void loadEvents();
  }, [loadEvents]);

  const filtered = events
    .filter((e) => {
      const matchSearch =
        e.title.toLowerCase().includes(search.toLowerCase()) ||
        e.city.toLowerCase().includes(search.toLowerCase());
      const matchStatus =
        filterStatus == null
          ? true
          : filterStatus === "actual"
            ? e.status !== "past"
            : e.status === filterStatus;
      const matchDates = eventOverlapsRange(e, dateFrom, dateTo);
      return matchSearch && matchStatus && matchDates;
    })
    .sort(
      filterStatus === "past" || filterStatus == null
        ? compareEventsForListing
        : compareEventsByNearestStart,
    );

  const currentFiltered = filtered.filter((e) => e.status !== "past");
  const pastFiltered = filtered.filter((e) => e.status === "past");
  const showPastDivider = filterStatus == null && pastFiltered.length > 0 && currentFiltered.length > 0;

  return (
    <div className="p-4 sm:p-6 max-w-5xl mx-auto space-y-5">
      {showCreate && canManageAllEvents(user) && (
        <CreateEventModal
          onClose={() => setShowCreate(false)}
          onCreated={(event) => setEvents((prev) => [event, ...prev])}
        />
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-[20px] text-gray-900">Мероприятия</h1>
          <p className="text-[13px] text-gray-400 mt-0.5">
            {loading
              ? "Загрузка…"
              : user?.role === "curator"
                ? `${events.length} мероприятий с вашим доступом`
                : `${events.length} мероприятий в системе`}
          </p>
        </div>
        {canManageAllEvents(user) && (
          <button
            onClick={() => setShowCreate(true)}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-[13px] transition-colors"
          >
            <Plus size={15} />
            Создать
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="space-y-2">
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
          <div className="flex items-center gap-2 flex-1 bg-white rounded-lg border border-gray-200 px-3 py-2">
            <Search size={15} className="text-gray-400 flex-shrink-0" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Поиск мероприятий..."
              className="flex-1 text-[13px] outline-none text-gray-700 placeholder-gray-400"
            />
          </div>
          <div className="flex items-center gap-1.5 overflow-x-auto" style={{ scrollbarWidth: "none" }}>
            {[ "actual", null, "active", "upcoming", "past"].map((s) => {
              const labels: Record<string, string> = {
                actual: "Актуальные",
                active: "Идут сейчас",
                upcoming: "Предстоящие",
                past: "Прошедшие",
              };
              return (
                <button
                  key={String(s)}
                  onClick={() => setFilterStatus(s)}
                  className={`flex-shrink-0 px-3 py-1.5 rounded-lg text-[12px] transition-colors ${
                    filterStatus === s
                      ? "bg-indigo-600 text-white"
                      : "bg-white border border-gray-200 text-gray-600 hover:border-indigo-200 hover:text-indigo-600"
                  }`}
                >
                  {s === null ? "Все" : labels[s]}
                </button>
              );
            })}
          </div>
        </div>
        {canManageAllEvents(user) && (
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
            <div className="flex items-center gap-2 bg-white rounded-lg border border-gray-200 px-3 py-2">
              <CalendarDays size={14} className="text-gray-400 flex-shrink-0" />
              <label className="text-[12px] text-gray-500 whitespace-nowrap">С</label>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="text-[13px] outline-none text-gray-700"
              />
              <label className="text-[12px] text-gray-500 whitespace-nowrap">по</label>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="text-[13px] outline-none text-gray-700"
              />
            </div>
            {(dateFrom || dateTo) && (
              <button
                onClick={() => {
                  setDateFrom("");
                  setDateTo("");
                }}
                className="px-3 py-1.5 rounded-lg text-[12px] text-gray-500 hover:bg-gray-100 border border-gray-200"
              >
                Сбросить период
              </button>
            )}
            <p className="text-[11px] text-gray-400 sm:ml-auto">
              Статус: активные — в текущем месяце · предстоящие — после · прошедшие — даты уже прошли
            </p>
          </div>
        )}
      </div>

      {/* Events grid — cover 16:9 + 8px sides (как превью в мини-апп) */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {filtered.flatMap((event, index) => {
          const nodes: React.ReactNode[] = [];
          if (
            showPastDivider &&
            event.status === "past" &&
            (index === 0 || filtered[index - 1]?.status !== "past")
          ) {
            nodes.push(
              <div
                key="past-divider"
                className="md:col-span-2 flex items-center gap-3 py-1"
              >
                <div className="h-px flex-1 bg-gray-200" />
                <span className="text-[11px] uppercase tracking-wide text-gray-400 flex-shrink-0">
                  Прошедшие
                </span>
                <div className="h-px flex-1 bg-gray-200" />
              </div>,
            );
          }
          const cfg = statusConfig[event.status];
          const companiesCount = event.companiesCount;
          nodes.push(
            <div
              key={event.id}
              className="bg-white rounded-xl border border-gray-100 overflow-hidden hover:border-indigo-100 transition-colors group flex flex-col"
            >
              <div className="border-b border-gray-50 bg-white">
                <AdminEventCover
                  src={eventCoverSrc(event)}
                  alt={event.title}
                  rounded="rounded-none"
                />
              </div>

              <div className="flex-1 p-3 sm:p-4 min-w-0 flex flex-col gap-2">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <h3 className="text-[14px] sm:text-[15px] text-gray-900">{event.title}</h3>
                      <span className={`flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full flex-shrink-0 ${cfg.bg} ${cfg.text}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
                        {cfg.label}
                      </span>
                      {event.stubMode && (
                        <span className="text-[10px] px-2 py-0.5 rounded-full flex-shrink-0 bg-amber-100 text-amber-800">
                          Заглушка
                        </span>
                      )}
                    </div>
                    <div className="flex flex-col gap-0.5 text-[12px] text-gray-400 mb-1">
                      <span className="flex items-center gap-1">
                        <MapPin size={11} /> {event.city} · {event.venueName}
                      </span>
                      <span className="flex items-center gap-1">
                        <CalendarDays size={11} /> {formatDate(event.startDate)} — {formatDate(event.endDate)}
                      </span>
                    </div>
                    <p className="text-[12px] text-gray-500 line-clamp-2">{event.description}</p>
                  </div>

                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button
                      onClick={() => navigate(`/events/${event.id}`)}
                      className="flex items-center gap-1 sm:gap-1.5 px-2 sm:px-3 py-1.5 rounded-lg bg-indigo-50 hover:bg-indigo-100 text-indigo-600 text-[12px] transition-colors"
                    >
                      <span className="hidden sm:inline">Открыть</span> <ArrowRight size={12} />
                    </button>
                    <div className="relative">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setOpenMenuId(openMenuId === event.id ? null : event.id);
                        }}
                        className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 transition-colors"
                      >
                        <MoreHorizontal size={16} />
                      </button>
                      {openMenuId === event.id && (
                        <div className="absolute right-0 top-full mt-1 w-40 bg-white rounded-xl shadow-lg border border-gray-100 py-1 z-10">
                          <button className="w-full flex items-center gap-2 px-3 py-2 text-[13px] text-gray-700 hover:bg-gray-50">
                            <Pencil size={13} /> Редактировать
                          </button>
                          <button className="w-full flex items-center gap-2 px-3 py-2 text-[13px] text-gray-700 hover:bg-gray-50">
                            <Copy size={13} /> Дублировать
                          </button>
                          <div className="border-t border-gray-50 my-1" />
                          <button className="w-full flex items-center gap-2 px-3 py-2 text-[13px] text-red-500 hover:bg-red-50">
                            <Trash2 size={13} /> Удалить
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-4 text-[12px] text-gray-500 pt-1">
                  <span className="flex items-center gap-1">
                    <Building2 size={12} /> <span className="text-gray-900">{companiesCount}</span> экспонентов
                  </span>
                  <span className="flex items-center gap-1">
                    <Users size={12} /> <span className="text-gray-900">{event.visitorsCount.toLocaleString("ru-RU")}</span> посетителей
                  </span>
                </div>
              </div>

              <div className="border-t border-gray-50 px-4 py-2 flex items-center gap-1 mt-auto">
                {[
                  { label: "Экспоненты", href: `/events/${event.id}/exhibitors` },
                  { label: "Карта", href: `/events/${event.id}/map` },
                  { label: "Программа", href: `/events/${event.id}/schedule` },
                  { label: "Аналитика", href: `/events/${event.id}/stats` },
                ].map((link) => (
                  <button
                    key={link.href}
                    onClick={() => navigate(link.href)}
                    className="px-2.5 py-1 rounded-lg text-[11px] text-gray-500 hover:bg-gray-100 hover:text-indigo-600 transition-colors"
                  >
                    {link.label}
                  </button>
                ))}
              </div>
            </div>,
          );
          return nodes;
        })}
      </div>
    </div>
  );
}
